<script setup lang="ts">
/**
 * A generated control for the free layout's board lists (plan §4.4, pilot-v1 R2-07): every shelf
 * (height of its underside from the floor, left end, right end) or every upright (left face from
 * the left edge, bottom, top) as millimetre fields, with Add and Remove. Values are stored as
 * fractions of the overall size (src/families/free-grid/design.ts) and converted here with the
 * board thickness and the feet. Every edit goes through the family and is refused beside the
 * list when the result would be invalid.
 */
import { computed } from 'vue';
import type { ParameterDef } from '@/families/types';
import { fmtMm, parseMm } from '@/domain';
import Icon from './Icon.vue';
import { snap, ui } from '@/app/state';
import { setFamilyParam } from '@/app/edits';
import { focusSoon } from '@/app/navigation';
import { freeFrame, type BackBoard, type Shelf, type Upright } from '@/families/free-grid';

const props = defineProps<{ p: ParameterDef }>();
const key = computed(() => props.p.key as 'shelves' | 'uprights' | 'backs');
const isShelf = computed(() => key.value === 'shelves');
const isBack = computed(() => key.value === 'backs');
const d = computed(() => snap.value.design);
const frame = computed(() => freeFrame(d.value));
const Hc = computed(() => d.value.H - frame.value.lift);
type Board = Shelf | Upright | BackBoard;
const list = computed<Board[]>(() => {
  const v = d.value.params?.[key.value];
  return Array.isArray(v) ? (v as Board[]) : [];
});
const r4 = (v: number) => Math.round(v * 1e5) / 1e5;

/** The three fields of a board, in display order, with their mm values and how to store them. */
type FieldKey = 'y' | 'x0' | 'x1' | 'x' | 'y0' | 'y1';
const FIELDS = computed<{ k: FieldKey; label: string }[]>(() => (isShelf.value
  ? [{ k: 'y', label: 'Height' }, { k: 'x0', label: 'From' }, { k: 'x1', label: 'To' }]
  : isBack.value
    ? [{ k: 'x0', label: 'From' }, { k: 'x1', label: 'To' }, { k: 'y0', label: 'Bottom' }, { k: 'y1', label: 'Top' }]
    : [{ k: 'x', label: 'From left' }, { k: 'y0', label: 'Bottom' }, { k: 'y1', label: 'Top' }]));
/** What the list calls one board. */
const nounCap = computed(() => (isShelf.value ? 'Shelf' : isBack.value ? 'Back board' : 'Upright'));
/** A stored fraction as mm-ticks from the floor or the left edge (heights include the feet). */
function toTicks(k: FieldKey, f: number): number {
  const { t, lift } = frame.value;
  // whole millimetres, as the generator places the boards
  const mmTicks = (v: number) => Math.round(v / 10) * 10;
  if (k === 'y') return lift + mmTicks(f * (Hc.value - t));
  if (k === 'x') return mmTicks(f * (d.value.W - t));
  if (k === 'x0' || k === 'x1') return mmTicks(f * d.value.W);
  return lift + mmTicks(f * Hc.value);
}
function toFrac(k: FieldKey, ticks: number): number {
  const { t, lift } = frame.value;
  if (k === 'y') return Math.max(0, ticks - lift) / (Hc.value - t);
  if (k === 'x') return ticks / (d.value.W - t);
  if (k === 'x0' || k === 'x1') return ticks / d.value.W;
  return Math.max(0, ticks - lift) / Hc.value;
}
const maxOf = (k: FieldKey) => (k === 'x0' || k === 'x1' || k === 'x' ? d.value.W : d.value.H);

/** Shelves top to bottom, uprights left to right, as the piece reads. */
const shown = computed(() => {
  const rows = list.value.map((b, i) => ({ i, b }));
  if (isBack.value) return rows.sort((a, c) => (a.b as BackBoard).x0 - (c.b as BackBoard).x0);
  return isShelf.value ? rows.sort((a, c) => (c.b as Shelf).y - (a.b as Shelf).y) : rows.sort((a, c) => (a.b as Upright).x - (c.b as Upright).x);
});
/** The board's name as the model and the parts list give it ("Top", "Shelf 2", "Left side", "Upright 1"). */
function nameOf(i: number): string {
  const parts = snap.value.build.parts;
  const id = isShelf.value ? `shelf-${i + 1}` : isBack.value ? `back-${i + 1}` : `upright-${i + 1}`;
  const part = parts.find((q) => q.id === id) ?? parts.find((q) => q.id.startsWith(`${id}-`));
  return part ? part.label.replace(/, piece \d+ of \d+$/, '') : `${nounCap.value} ${i + 1}`;
}
const draftId = (i: number, k: FieldKey) => `bd-${key.value}-${i}-${k}`;
const val = (i: number, k: FieldKey, f: number) => (draftId(i, k) in ui.drafts ? ui.drafts[draftId(i, k)]! : fmtMm(toTicks(k, f)));
const err = computed(() => ui.fieldErrors[key.value] ?? null);

function commit(i: number, k: FieldKey, raw: string) {
  const r = parseMm(raw);
  if (!r.ok) { ui.fieldErrors[key.value] = r.error; return; }
  if (r.ticks < 0 || r.ticks > maxOf(k)) { ui.fieldErrors[key.value] = `Between 0 and ${fmtMm(maxOf(k))} mm.`; return; }
  const next = list.value.map((b, j) => (j === i ? { ...b, [k]: r4(toFrac(k, r.ticks)) } : { ...b }));
  if (setFamilyParam(key.value, next, `${nameOf(i)}: ${FIELDS.value.find((f) => f.k === k)!.label.toLowerCase()} ${fmtMm(r.ticks)} mm`)) delete ui.drafts[draftId(i, k)];
}
function onKey(e: KeyboardEvent, i: number, k: FieldKey) {
  const el = e.target as HTMLInputElement;
  if (e.key === 'Enter') { e.preventDefault(); commit(i, k, el.value); return; }
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  const r = parseMm(el.value);
  if (!r.ok) return;
  e.preventDefault();
  const next = fmtMm(Math.max(0, r.ticks + (e.shiftKey ? 100 : 10) * (e.key === 'ArrowUp' ? 1 : -1)));
  ui.drafts[draftId(i, k)] = next;
  el.value = next;
  commit(i, k, next);
}
function remove(i: number) {
  focusSoon(`[data-board-add="${key.value}"]`);
  setFamilyParam(key.value, list.value.filter((_b, j) => j !== i), `${nameOf(i)} removed`);
}
/** A new board goes across the tallest opening: a shelf through its middle, or an upright up its middle. */
function add() {
  const { lift } = frame.value;
  const open = snap.value.build.openings.slice().sort((a, b) => (b.bounds.y1 - b.bounds.y0) * (b.bounds.x1 - b.bounds.x0) - (a.bounds.y1 - a.bounds.y0) * (a.bounds.x1 - a.bounds.x0))[0];
  const W = d.value.W / 10, H = Hc.value / 10, L = lift / 10;
  let board: Board;
  if (isBack.value) {
    // a back board behind the largest opening
    const b = open?.bounds ?? { x0: 0, x1: W, y0: L, y1: L + H };
    board = { x0: r4(b.x0 / W), x1: r4(b.x1 / W), y0: r4((b.y0 - L) / H), y1: r4((b.y1 - L) / H) };
  } else if (isShelf.value) {
    const b = open?.bounds ?? { x0: 0, x1: W, y0: L, y1: L + H };
    board = { y: r4(((b.y0 + b.y1) / 2 - L) / H), x0: r4(b.x0 / W), x1: r4(b.x1 / W) };
  } else {
    const b = open?.bounds ?? { x0: 0, x1: W, y0: L, y1: L + H };
    board = { x: r4(toFrac('x', Math.round(((b.x0 + b.x1) / 2) * 10 - frame.value.t / 2))), y0: r4((b.y0 - L) / H), y1: r4((b.y1 - L) / H) };
  }
  focusSoon(`[data-board-add="${key.value}"]`);
  setFamilyParam(key.value, [...list.value, board], `${nounCap.value} added`);
}
</script>

<template>
  <div class="rowlist-ctl" :data-boardlist="key">
    <div class="rowdiv-head">
      <h3 :id="`ph-${key}`" class="h-sub">{{ p.ui.label }}</h3>
      <button type="button" class="btn btn--sm btn--quiet" :data-board-add="key" @click="add"><Icon name="plus" /> Add {{ isShelf ? 'a shelf' : isBack ? 'a back board' : 'an upright' }}</button>
    </div>
    <p class="hint">{{ isShelf ? 'Height is the underside from the floor; From and To are the ends, from the left edge.' : isBack ? 'From and To are the edges from the left; Bottom and Top are from the floor.' : 'From left is the upright’s left face; Bottom and Top are from the floor.' }} {{ p.ui.help }}</p>
    <p v-if="isBack && !list.length" class="rowdiv-none">No back boards: the back is open.</p>
    <ol class="rowdivs" :aria-labelledby="`ph-${key}`">
      <li v-for="r in shown" :key="r.i" class="rowdiv" :data-board="`${key}-${r.i}`">
        <div class="rowdiv-list" role="group" :aria-label="nameOf(r.i)">
          <strong class="rowdiv-name">{{ nameOf(r.i) }}</strong>
          <span v-for="f in FIELDS" :key="f.k" class="rowdiv-item">
            <label class="lbl lbl--inline" :for="draftId(r.i, f.k)">{{ f.label }}<span class="sr-only">, {{ nameOf(r.i) }}</span></label>
            <span class="input-unit input-unit--sm">
              <input
                :id="draftId(r.i, f.k)"
                class="input mono"
                inputmode="decimal"
                autocomplete="off"
                :value="val(r.i, f.k, Number((r.b as unknown as Record<string, number>)[f.k]))"
                :aria-invalid="err ? 'true' : undefined"
                :aria-describedby="err ? `err-${key}` : undefined"
                @input="ui.drafts[draftId(r.i, f.k)] = ($event.target as HTMLInputElement).value"
                @change="($event.target as HTMLInputElement).isConnected && commit(r.i, f.k, ($event.target as HTMLInputElement).value)"
                @keydown="onKey($event, r.i, f.k)"
              ><span>mm</span>
            </span>
          </span>
          <button type="button" class="icon-btn icon-btn--sm" :data-board-remove="`${key}-${r.i}`" :aria-label="`Remove ${nameOf(r.i).toLowerCase()}`" @click="remove(r.i)"><Icon name="close" /></button>
        </div>
      </li>
    </ol>
    <p v-if="err" :id="`err-${key}`" class="field-err" role="alert"><Icon name="alert" /><span>{{ err }}</span></p>
  </div>
</template>
